import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["reply", "attachments", "getDisabledProps", "maxSize", "onSubmit", "originalGenericChannelId", "auth", "threadId", "prosemirrorConfig", "isConfigLoading", "replyRecommendationsEnabled"];
import { useEffect, useCallback, useMemo, useState, useRef, useContext } from 'react';
import { getCommonMessage, getReplyId } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { getIsForward } from 'conversations-message-history/common-message-format/operators/emailMetadataGetters';
import { createEmailReplyHistoryPlugin } from 'hubspot-prosemirror/prosemirror/plugins/EmailReplyHistory';
import debounce from 'hs-lodash/debounce';
import { RichTextEditor } from '../../../prosemirror/protected/components';
import { getTracker, getTrackingContext } from '../../../usage-tracking/clients/usageTracker';
import { sendDisabledReasonToErrorMessage } from '../../../disable-send/public/operators';
import { withRenderTimeMetrics } from '../../../metrics/protected/wrappers';
import { useMessageBodyDisabledConfig } from '../hooks/useMessageBodyDisabledConfig';
import { ReplyRecommendationManager } from '../../../reply-recommendations/protected/components';
import { THREAD_COMMENT } from 'conversations-message-history/comment-message/constants/messageTypes';
import { useBaseEditorState, useEditorStateDraft, useShouldIncludeEmailReplies } from '../../../editor-state/protected/hooks';
import { editorStateHasContent } from '../../../editor-state/protected/operators';
import { emailReplyHistoryClass } from 'hubspot-prosemirror/prosemirror/ProsemirrorSchema';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RichTextEditorWithRenderMetrics = withRenderTimeMetrics(RichTextEditor, 'compose-editor-rendering');
const EditorStateProvider = props => {
  const {
      reply,
      attachments,
      getDisabledProps,
      maxSize,
      onSubmit,
      originalGenericChannelId,
      auth,
      threadId,
      prosemirrorConfig,
      isConfigLoading,
      replyRecommendationsEnabled
    } = props,
    restOfRTEProps = _objectWithoutPropertiesLoose(props, _excluded);
  const commonMessage = getCommonMessage(reply);
  const replyId = getReplyId(reply);
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
  const [draft, save, clear] = useEditorStateDraft(replyId);
  const [editorState, setEditorState, resetEditorState] = useBaseEditorState(reply, draft);
  const internalEditorStateSetterRef = useRef(() => {});
  const [hasContent, setHasContent] = useState(false);
  const richTextEditorRef = useRef(null);
  const debouncedHandleHasContent = useMemo(() => debounce((nextEditorState, nextAttachments) => {
    const currentContent = editorStateHasContent(nextEditorState, nextAttachments);
    setHasContent(currentContent);
  }, 2000), [setHasContent]);
  const handleOnEditorStateChange = useCallback(nextEditorState => {
    setEditorState(nextEditorState);
    save(nextEditorState);
    if (!hasContent && editorStateHasContent(nextEditorState, attachments)) {
      debouncedHandleHasContent.flush();
      setHasContent(true);
    } else {
      debouncedHandleHasContent(nextEditorState, attachments);
    }
  }, [setEditorState, debouncedHandleHasContent, hasContent, attachments, save]);
  const handleOnSubmit = useCallback(({
    html,
    plainText,
    options
  }) => {
    onSubmit({
      html,
      plainText,
      options
    });
    resetEditorState();
    setHasContent(false);
    clear();
  }, [onSubmit, resetEditorState, clear]);
  const {
    disabled: disableSubmit,
    sendDisabledReason,
    options
  } = getDisabledProps({
    hasContent,
    editorState,
    maxSize
  });
  const disableSubmitMessage = sendDisabledReason && sendDisabledReasonToErrorMessage(sendDisabledReason, options);
  const disabledAwaitingUserAction = disableSubmit && sendDisabledReason === 'NO_CONTENT';
  useEffect(() => {
    setHasContent(editorStateHasContent(editorState, attachments));
  }, [editorState, attachments, setHasContent]);
  useEffect(() => {
    return () => debouncedHandleHasContent.cancel();
  }, [debouncedHandleHasContent]);
  const trackingContext = getTrackingContext();
  trackingContext.originalGenericChannelId = originalGenericChannelId;
  const {
    disabledConfig
  } = useMessageBodyDisabledConfig();
  const {
    genericChannelId
  } = useContext(ChannelCapabilitiesConfigContext);
  const validThreadId = Boolean(Number(threadId));
  const shouldDisplayReplyRec = replyRecommendationsEnabled && genericChannelId !== THREAD_COMMENT && validThreadId;
  const shouldIncludeEmailReplies = useShouldIncludeEmailReplies(commonMessage);
  const isForward = getIsForward(commonMessage);
  const plugins = useMemo(() => {
    const result = [];
    if (shouldIncludeEmailReplies) {
      result.push(createEmailReplyHistoryPlugin({
        // @ts-expect-error inReplyToId is actually a string. we should update the type
        conversationsMessageId: commonMessage.inReplyToId,
        inReplyToDisposition: isForward ? 'FORWARD' : 'REPLY'
      }));
    }
    return result;
  }, [commonMessage.inReplyToId, isForward, shouldIncludeEmailReplies]);
  const initialHtml = shouldIncludeEmailReplies ? `<p/><div class="${emailReplyHistoryClass}"></div>` : '';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldDisplayReplyRec ? /*#__PURE__*/_jsx(ReplyRecommendationManager, {
      editorState: editorState,
      onSubmit: handleOnSubmit,
      shouldDisableSubmit: disableSubmit && sendDisabledReason !== 'NO_CONTENT',
      disableSubmitMessage: disableSubmitMessage,
      setEditorState: internalEditorStateSetterRef.current,
      threadId: threadId
    }) : null, /*#__PURE__*/_jsx(RichTextEditorWithRenderMetrics, Object.assign({
      auth: auth,
      editorState: editorState,
      setEditorState: handleOnEditorStateChange,
      attachments: attachments,
      shouldDisableSubmit: disableSubmit,
      disabledConfig: disabledConfig,
      disableSubmitMessage: disableSubmitMessage,
      disabledAwaitingUserAction: disabledAwaitingUserAction,
      prosemirrorConfig: prosemirrorConfig,
      richTextEditorRef: richTextEditorRef,
      onSubmit: handleOnSubmit,
      tracker: getTracker(),
      trackingContext: trackingContext,
      canUseProsemirror: true,
      internalEditorStateSetterRef: internalEditorStateSetterRef,
      isConfigLoading: isConfigLoading,
      plugins: plugins,
      initialHtml: initialHtml
    }, restOfRTEProps))]
  });
};
EditorStateProvider.displayName = 'EditorStateProvider';
export default EditorStateProvider;